/* eslint-disable react/no-array-index-key */
/* eslint-disable no-restricted-syntax */
/* eslint-disable max-len */
import { CircularProgress, makeStyles } from '@material-ui/core';
import { BaseListCompDataItem, capitalizeAll, GenericTableComp, GenericTableCompProps, ITEMS_PER_PAGE_10, ITEMS_PER_PAGE_LIST, PaginationBar,
    PAGING_MIN_PAGES, useSearchParams, GenericMessageBox, BaseListCompField } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { CSSProperties, useCallback, useMemo } from 'react';
import { UIFetchEntityFilters } from '@moderepo/mode-data-state-user-app';
import { Entity, EntityClass, EntitySortFieldType, SortOrder } from '@moderepo/mode-internal-apis';
import { produce } from 'immer';
import { EntityUIComponentCollection, EntityUIComponentWithChildrenProps, SORTABLE_CUSTOM_ATTRIBUTE_TYPES, useSelectTranslatedEntityClasses,
    splitEntitySortFieldInfo, SORTABLE_BASIC_ENTITY_ATTRIBUTES } from '..';
import { useSelectEntity, useSelectEntityChildren, useSelectProject } from '../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue, Mutable } from '../../../utils/utils';
import { NO_VALUE } from '../../../utils/constants';
import { BASIC_ENTITY_ATTRIBUTES_SORT_FIELD_OVERRIDE_MAP, EntityDataSettings, EntityDataSource, isCustomAttributeEntityDataSettings, isEntityDataSettings, isSubTypeEntityDataSettings,
    isTSDBEntityDataSettings, useGetEntitiesData } from '.';
import { useRitzTranslation } from '../../../utils/customHooks';
import { SettingsErrorLogger } from '../../../utils/logger';



const useStyle = makeStyles(() => {
    return {
        root: {
        },
    };
}, { name: 'EntityChildrenSummaryTableUIComponent', index: 1 });


/**
 * For EntityTableDataSettings will be used for configuring the entity data for the table component. It will be similar to the EntityDataSettings
 * except that it will require additional attributes therefore we can extends the EntityDataSettings
 */
interface EntityTableDataSettings extends EntityDataSettings {
    // The column label translation key to use for one entity data
    readonly columnLabelKey: string;
}


/**
 * Type guard function to make sure an object is a EntityTableDataSettings object
 */
const isEntityTableDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityTableDataSettings => {
    const settings = obj as EntityTableDataSettings;
    const configPath = [...(currentConfigPath ?? [])];


    // "EntityTableDataSettings" extends "EntityDataSettings" therefore it must satisfy the "isEntityDataSettings" type guard
    if (!isEntityDataSettings(settings, errorLogger, configPath)) {
        return false;
    }

    // Add additional checks specifically for "EntityTableDataSettings"
    if (!isValidSettingStringValue(settings.columnLabelKey, true, false)) {
        return errorLogger?.logError({
            message: '"columnLabelKey" must be a non-empty string.',
            value  : settings.columnLabelKey,
            path   : [...configPath, 'columnLabelKey'],
        }) ?? false;
    }

    return true;
};



/**
 * This is the interface for the EntityChildrenSummaryTableUIComponent's CustomSettings.
 */
export interface EntityChildrenSummaryTableUIComponentCustomSettings {
    // Custom title and icon
    readonly titleKey?: string | undefined;
    readonly icon?: string | undefined;

    // The number of items to show per page.
    readonly pageSize?: number | undefined;

    // To specify how many decimal places to round the value to. This is global setting that apply to all data EXCEPT for data that have
    // their own custom decimalPlaces setting.
    readonly decimalPlaces?: number | undefined;

    // The default value to display when an data does not have a value. This is a global setting that apply to all data EXCEPT for data that
    // have their own defaultValueKey setting.
    readonly defaultValueKey?: string | undefined;

    // An array of data to be displayed. The data will be displayed in the same order as they appear in this array.
    readonly displayData: readonly EntityTableDataSettings[];

    // The custom CSS style for the outer container element
    readonly containerStyle?: CSSProperties | undefined;

    // Whether or not to enable the "Preview" button that shows the Entity Preview panel. The default is FALSE.
    readonly allowPreview?: boolean | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of EntityChildrenSummaryTableUIComponentCustomSettings
 */
export const isEntityChildrenSummaryTableUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityChildrenSummaryTableUIComponentCustomSettings => {
    const settings = obj as EntityChildrenSummaryTableUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.decimalPlaces, false, false, 0, 10)) {
        return errorLogger?.logError({
            message: '"decimalPlaces" must be undefined or an integer between 0 and 10 inclusive',
            value  : settings.decimalPlaces,
            path   : [...configPath, 'decimalPlaces'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultValueKey, false, true)) {
        return errorLogger?.logError({
            message: '"defaultValueKey" must be undefined or a string.',
            value  : settings.defaultValueKey,
            path   : [...configPath, 'defaultValueKey'],
        }) ?? false;
    }

    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }

    // Make sure pageSize is one of the predefined page sizes if it is provided
    if (
        settings.pageSize !== undefined
        && (!isValidSettingNumberValue(settings.pageSize, false, false) || !ITEMS_PER_PAGE_LIST.includes(settings.pageSize))
    ) {
        return errorLogger?.logError({
            message: `"pageSize" must be undefined or one the following numbers [${Object.values(
                ITEMS_PER_PAGE_LIST,
            ).map((pageSize) => {
                return `${pageSize}`;
            }).join(', ')}].`,
            value: settings.pageSize,
            path : [...configPath, 'pageSize'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.allowPreview)) {
        return errorLogger?.logError({
            message: '"allowPreview" must be undefined or a boolean',
            value  : settings.allowPreview,
            path   : [...configPath, 'allowPreview'],
        }) ?? false;
    }

    // Validate the "displayData" array
    if (!(Array.isArray(settings.displayData)) || settings.displayData.length === 0) {
        return errorLogger?.logError({
            message: '"displayData" is required and must be a non-empty array.',
            value  : settings.displayData,
            path   : [...configPath, 'displayData'],
        }) ?? false;
    }

    // Make sure every element in the "displayData" are valid
    if (!settings.displayData.every((dataSettings, index) => {
        // Return "true" if dataSettings is one of the following type of EntityDataSettings.
        if (!isSubTypeEntityDataSettings(dataSettings, errorLogger, [...configPath, 'displayData', `[${index}]`])) {
            return false;
        }

        if (!isEntityTableDataSettings(dataSettings, errorLogger, [...configPath, 'displayData', `[${index}]`])) {
            return false;
        }

        return true;
    })) {
        return false;
    }

    return true;
};



/**
 * This is a helper function to check if the dataName is sortable based on it's dataSourceType.
 * An Entity Summary Table can show data from different sources. The data can be data from basic_attributes, custom_attributes, tsdb,
 * descendant_counts and not all data are sortable. This function will determine if a data is sortable based on the provided info about the data.
 */
const isDataSortable = (dataSourceType: EntityDataSource, dataName: string, entityClasses: readonly EntityClass[]): boolean => {
    if (dataSourceType === EntityDataSource.BASIC_ATTRIBUTE) {
        // If the dataSource type is BASIC_ATTRIBUTE, the dataName MUST be one of the supported attribute names e.g. "entityId", "entityClass", etc...
        return SORTABLE_BASIC_ENTITY_ATTRIBUTES.includes(dataName);
    }
    if (dataSourceType === EntityDataSource.DESCENDANT_COUNTS) {
        // If the dataSource type is DESCENDANT_COUNTS, all dataName are sortable
        return true;
    }
    if (dataSourceType === EntityDataSource.CUSTOM_ATTRIBUTE) {
        // For CUSTOM_ATTRIBUTE data source, only the attribute which schema's value type is in SORTABLE_CUSTOM_ATTRIBUTE_TYPES list are sortable
        // For example, the attribute which schema's value type is "number", "string", or "boolean" are sortable
        // 1 problem, with only the dataName, we don't know what value type it is. We need to find the attribute schema for this dataName.
        // Another problem is that to find the attribute schema, we need to know the entity class the attribute schema belongs to.
        // And another problem is that it is possible that multiple entity classes have the same attribute schema with the same name however,
        // 1 is type "string" and 1 is type "array". If this happen, we have to decide whether or not this attribute is sortable. And we will
        // make it sortable if one of them is sortable.
        for (const entityClass of entityClasses) {
            // Find the schema in this entityClass that has the same name as the dataName
            const schema = entityClass.attributeSchema?.find((schema) => {
                return schema.key === dataName;
            });

            // If the schema's type if one of the type that can be sorted than add the dataName to the list of sortable attributes
            if (schema && SORTABLE_CUSTOM_ATTRIBUTE_TYPES.includes(schema.type)) {
                return true;
            }
        }
    }

    return false;
};



export interface EntityChildrenSummaryTableUIComponentProps extends EntityUIComponentWithChildrenProps {
    readonly customSettings?: EntityChildrenSummaryTableUIComponentCustomSettings | undefined;
    readonly itemsPerPage?: number | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity's children's summary e.g. name, attribute values, tsdb values, etc...
 * in Tabular format.
 */
export const EntityChildrenSummaryTableUIComponent: React.FC<EntityChildrenSummaryTableUIComponentProps> = (
    props: EntityChildrenSummaryTableUIComponentProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const classes = useStyle();


    // State for managing the pagination and filters
    const {
        searchParams, onItemsPerPageChange, onPrevPage, onNextPage, onPage, onOtherFieldsChange,
    } = useSearchParams<{
        readonly sortField?: string | undefined;
        readonly sortOrder?: SortOrder | undefined;
    }>({
        searchText  : '',
        itemsPerPage: props.customSettings?.pageSize ?? props.itemsPerPage ?? ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            sortField: undefined,
            sortOrder: undefined,
        },
    });

    // Create and cache the filters to be passed to the "useSelectEntityChildren" hook. We don't want to create this object dynamically
    // every time we call the "useSelectEntityChildren" because that will cause it to make unnecessary API calls.
    const currentFetchEntitiesFilters = useMemo((): UIFetchEntityFilters => {
        const { sortFieldType, sortField } = splitEntitySortFieldInfo(searchParams.otherFields.sortField);

        return {
            pageNumber: searchParams.currentPage,
            pageSize  : searchParams.itemsPerPage,
            sortFieldType,
            sortField,
            sortOrder : searchParams.otherFields.sortOrder,
        };
    }, [searchParams.currentPage, searchParams.itemsPerPage, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder]);


    // Get all children entities based on the currentFetchEntitiesFilters
    const childEntities = useSelectEntityChildren(
        props.projectId,
        props.entityId,
        currentFetchEntitiesFilters,
    );


    /**
     * Compile the map of sortable data name and group them by data source type. The result should be something like this
     *
     *      sortableDataByDataSourceType = {
     *          base_attributes: {
     *              entityClass: true,
     *              entityId: true,
     *          },
     *          custom_attributes: {
     *              serial_number: true,
     *              status: true,
     *          },
     *          descendant_counts: {
     *              site: true,
     *              feeder: true,
     *              nozzle: true
     *          }
     *      }
     */
    const sortableDisplayDataByDataSourceType = useMemo((): {
        readonly [dataSourceType: string]: {
            readonly [fieldName: string]: boolean | undefined;
        } | undefined;
    } | undefined => {
        if (entityClasses) {

            const sortableAttributes = {
                [EntityDataSource.BASIC_ATTRIBUTE]  : {},
                [EntityDataSource.CUSTOM_ATTRIBUTE] : {},
                [EntityDataSource.DESCENDANT_COUNTS]: {},
            };

            // Go through the list of displayData and check if they can actually be sorted. For the displayData that can be sorted, add them to
            // the sortableAttributes map. If the a displayData can't be sorted because of some rule then we will not make that data sortable.
            props.customSettings?.displayData?.forEach((displayData) => {
                const dataSourceType = displayData.source;
                const dataName = displayData.name;
                const sortable = isDataSortable(dataSourceType, dataName, entityClasses);
                if (sortable) {
                    sortableAttributes[dataSourceType][dataName] = true;
                }
            });

            return sortableAttributes;
        }

        return undefined;
    }, [entityClasses, props.customSettings?.displayData]);


    // Because we need to show the latest value for all the data, we will use the current time as the date which we need to fetch data for.
    const date = useMemo(() => {
        return Date.now();
    }, []);


    // First, call the "useGetEntitiesData" to get the values for all the data we need to display for all the entities
    const multipleEntitiesData = useGetEntitiesData(
        props.projectId,
        childEntities?.items,
        entityClasses,
        props.customSettings?.displayData,
        date,
    );


    /**
     * On sort column clicked, we will update the sortField and sortOrder based on the column the user clicked.
     */
    const onSortColumnClicked = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft) => {
                if (field.dataItemProp) {
                    if (draft.sortField === field.dataItemProp) {
                        // this column is currently being sorted so don't need to change the "sortField"
                        // But we need to change/toggle the sort order
                        if (draft.sortOrder === SortOrder.ASC) {
                            draft.sortOrder = SortOrder.DESC;
                        } else {
                            draft.sortOrder = SortOrder.ASC;
                        }
                    } else {
                        // This column is not the current column being sorted, change the "sortField"
                        draft.sortField = field.dataItemProp;
                        if (!draft.sortOrder) {
                            draft.sortOrder = SortOrder.ASC;
                        }
                    }
                }
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);



    /**
     * Because back end uses different strings for sortFieldType than front end, we need a mapping to map front end values to back end values
     * Front end uses "custom_attribute" and "descendant_counts" while back end uses "attributes" and "descendantCounts"
     */
    const ENTITY_DATA_SOURCE_TYPE_TO_SORT_FIELD_TYPE_MAPPING = useMemo(() => {
        return {
            [EntityDataSource.BASIC_ATTRIBUTE]  : EntitySortFieldType.BASIC,
            [EntityDataSource.CUSTOM_ATTRIBUTE] : EntitySortFieldType.ATTRIBUTES,
            [EntityDataSource.DESCENDANT_COUNTS]: EntitySortFieldType.DESCENDANT_COUNTS,
        };
    }, []);


    /**
     * Given a "dataSource" and a "dataName", return a unique name by combining the prefix to be used for the dataSource and the data name.
     * This is needed because it is possible for multiple data source to have the same name. For example, an Entity object has an attribute
     * calls "name", this is the entity's BASIC attribute. And an entity can also have a CUSTOM attribute call "name" as well. So if the admin
     * add both of these attributes to the table, there will be a naming conflict if we only use the attribute's name as the table column
     * name. Therefore, we must prepend the attribute name with with a prefix based on the data source type. For example:
     *      For BASIC attribute, we will prepend the prefix "basic". So "name" will be "basic:name"
     *      For CUSTOM attributes, we will prepend the prefix "attributes". So the "name" will be "attributes.name"
     *      For DESCENDANT_COUNTS, we will prepend the prefix "descendantCounts". So the "driver" will be "descendantCounts.driver"
     */
    const getUniqueDataName = useCallback((dataSource: EntityDataSource, dataName: string): string => {
        const prefix = ENTITY_DATA_SOURCE_TYPE_TO_SORT_FIELD_TYPE_MAPPING[dataSource];
        return `${prefix}:${BASIC_ENTITY_ATTRIBUTES_SORT_FIELD_OVERRIDE_MAP[dataName] ?? dataName}`;
    }, [ENTITY_DATA_SOURCE_TYPE_TO_SORT_FIELD_TYPE_MAPPING]);



    /**
     * Create the columns for the table. We will create 1 column for each dataSettings in the "customSettings.displayData" array.
     */
    const fieldsSettings = useMemo((): GenericTableCompProps<Entity>['fieldsSettings'] => {
        if (props.customSettings?.displayData) {

            const fields: Mutable<GenericTableCompProps<Entity>['fieldsSettings']['fields']> = {};

            if (props.customSettings?.allowPreview) {
                fields.preview = {
                    label       : '',
                    dataItemProp: 'preview',
                };
            }

            props.customSettings.displayData.forEach((dataSettings) => {
                const uniqueColumnName = getUniqueDataName(dataSettings.source, dataSettings.name);

                fields[uniqueColumnName] = {
                    label       : trans(dataSettings.columnLabelKey),
                    dataItemProp: uniqueColumnName,
                    sortable    : sortableDisplayDataByDataSourceType?.[dataSettings.source]?.[dataSettings.name],
                    valueType   : dataSettings.valueType,
                };
            });

            return {
                fields,
                onSortByField     : onSortColumnClicked,
                currentSortedField: searchParams.otherFields.sortField,
                currentSortedOrder: searchParams.otherFields.sortOrder,
            };
        }
        return {
            fields: {},
        };
    }, [getUniqueDataName, onSortColumnClicked, props.customSettings?.allowPreview, props.customSettings?.displayData,
        searchParams.otherFields.sortField, searchParams.otherFields.sortOrder, sortableDisplayDataByDataSourceType, trans]);


    /**
     * "multipleEntitiesData" contains the data for all entities however, it is not in the format that the "GenericTableComp" can use therefore,
     * this function will convert "multipleEntitiesData" to the data format that "GenericTableComp" can use.
     */
    const formattedEntitiesData = useMemo((): readonly BaseListCompDataItem<Entity>[] | undefined => {
        if (multipleEntitiesData) {

            const result: BaseListCompDataItem<Entity>[] = [];

            Object.values(multipleEntitiesData).forEach((oneEntityData, index) => {
                const entity = childEntities?.items[index];
                if (entity && oneEntityData) {

                    /**
                     * "oneEntityData" is an array of EntityData objects for 1 entity. The "GenericTableComp" doesn't work with this format.
                     * The data needs to be a mapped of metricName => value. Therefore, we need to convert the "oneEntityData" array into an object.
                     * For example: Given
                     *      oneEntityData = [
                     *          {name:"temperature", value: 56, unit: "celsius"},
                     *          {name:"pressure", value: 46, unit: "hPa"},
                     *          {name:"humidity", value: 79, unit: "g/m3"}
                     *      ]
                     *
                     *      mergedEntityData = {
                     *          temperature: "56 celsius",
                     *          pressure: "46 hPa",
                     *          humidity: "79 g/m3"
                     *      }
                     */
                    const mergedEntityData: {
                        [metricName: string]: string;
                    } = {};

                    oneEntityData.forEach((data) => {

                        // If dataSettings has decimalPlaces setting then use it. Else use the global decimalPlaces settings
                        const decimalPlaces = data
                                        && (isCustomAttributeEntityDataSettings(data.settings) || isTSDBEntityDataSettings(data.settings))
                                        && data.settings.decimalPlaces !== undefined
                            ? data.settings.decimalPlaces
                            : props.customSettings?.decimalPlaces;

                        // Get the "defaultValue" to be used if the data does not have a value
                        const defaultValueKey = data?.settings.defaultValueKey ?? props.customSettings?.defaultValueKey;
                        const defaultValue = defaultValueKey !== undefined ? trans(defaultValueKey) : NO_VALUE;

                        // Get the value from the data object and format it if necessary
                        const value = data?.value;

                        const displayValue = value !== undefined && typeof value === 'number' && decimalPlaces !== undefined
                            ? trans('{{value, number-format}}', {
                                value,
                                decimalPlaces,
                            })
                            : (value ?? defaultValue)?.toString();

                        // Add the unit to the displayValue but only if the value is not undefined..
                        const displayValueWithUnit = value !== undefined && data.unit !== undefined
                            ? trans('label.value_unit_display', { value: displayValue, unit: data.unit })
                            : displayValue;

                        // Add data value to the result using the data's metric's name as the key.
                        const uniqueDataName = getUniqueDataName(data.settings.source, data.settings.name);
                        mergedEntityData[uniqueDataName] = data.settings.valueType === 'image' ? value : displayValueWithUnit;
                    });


                    result.push({
                        actualValue : entity,
                        displayValue: mergedEntityData,
                    });
                }
            });

            return result;
        }

        return undefined;
    }, [childEntities?.items, getUniqueDataName, multipleEntitiesData, props.customSettings?.decimalPlaces, props.customSettings?.defaultValueKey,
        trans]);


    // Create PaginationBar and cache it. We will pass this component to the GenericTableComp.
    const paginationComp = useMemo((): GenericTableCompProps<Entity>['paginationComp'] => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : childEntities && childEntities.range?.total,
                hasMoreItems    : (childEntities && childEntities.range
                    ? childEntities.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (!childEntities || childEntities.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage    : capitalizeAll(trans('label.paging.items_per_page', { item_name: trans('label.item', { count: 2 }) })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [searchParams.itemsPerPage, searchParams.currentPage, childEntities, trans, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);


    return (
        <>
            {selectedEntity && childEntities && formattedEntitiesData ? (
                <GenericTableComp
                    className={clsx(props.className, classes.root)}
                    style={{
                        ...props.style,
                        ...props.customSettings?.containerStyle,
                    }}
                    title={props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : undefined}
                    icon={props.customSettings?.icon}
                    headerless={!props.customSettings?.titleKey && !props.customSettings?.icon}
                    fieldsSettings={fieldsSettings}
                    listData={formattedEntitiesData}
                    paginationComp={paginationComp}
                    onListItemSelected={props.onShowEntityDetails}
                    onPreviewItemClicked={props.onShowEntityPreview}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.data', { count: 2 })),
                            })}
                        />
                    )}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.data', { count: 2 })),
                            })}
                        />
                    )}
                />
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityChildrenSummaryTableUIComponent', EntityChildrenSummaryTableUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Children_Summary_Table_Comp',
    {
        componentFunc          : EntityChildrenSummaryTableUIComponent,
        customSettingsValidator: isEntityChildrenSummaryTableUIComponentCustomSettings,
    },
);
